import React from "react";
import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { useQueryBackgroundChecksSettings } from "../../../../query/backgroundChecks/queries/useQueryBackgroundChecksSettings";
import { useQuerySharedFileSettings } from "../../../../query/sharedFile/query/useQuerySharedFileSettings";
import { WSServiceError } from "../../../../utils/serviceHelper";
import { getBackgroundChecksDefaultValues } from "../../modules/BackgroundChecks/editRequirementDefinition/getBackgroundChecksDefaultValues";
import { getSharedFileDefaultValues } from "../../modules/SharedFile/editRequirementDefinition/getSharedFileDefaultValues";
import { useEditBackgroundChecksRequirementDefinition } from "../../modules/BackgroundChecks/editRequirementDefinition/useEditBackgroundChecksRequirementDefinition";
import { useEditSharedFileRequirementDefinition } from "../../modules/SharedFile/editRequirementDefinition/useEditSharedFileRequirementDefinition";

type FormDefaultDataLoaderQueryResult = {
  loading: boolean;
  error: WSServiceError | null | unknown;
  editError: WSServiceError | null | unknown;
  onSubmit: (formData: any) => Promise<void>;
  data: ReturnType<typeof getBackgroundChecksDefaultValues> | undefined;
};

interface FormDefaultDataLoaderProps<
  RT extends RequirementType = RequirementType
> {
  requirementType: RT;
  dataSourceId: RT extends
    | RequirementType.BackgroundCheck
    | RequirementType.SharedFile
    | RequirementType.Signature
    ? string
    : undefined;
  children: (props: FormDefaultDataLoaderQueryResult) => React.ReactNode;
}

export const FormDefaultDataLoader: React.FC<FormDefaultDataLoaderProps> = ({
  requirementType,
  dataSourceId,
  children
}) => {
  const backgroundChecksSettingsQuery = useQueryBackgroundChecksSettings(
    dataSourceId!,
    { enabled: requirementType === RequirementType.BackgroundCheck }
  );
  const editBackgroundChecksRequirementDefinition =
    useEditBackgroundChecksRequirementDefinition({
      dataSourceId: dataSourceId!
    });

  const sharedFileSettingsQuery = useQuerySharedFileSettings(dataSourceId!, {
    enabled: requirementType === RequirementType.SharedFile
  });
  const editSharedFileRequirementDefinition =
    useEditSharedFileRequirementDefinition({
      dataSourceId: dataSourceId!
    });

  // Map RequirementType to a hook function that returns QueryResult
  const hookMap: Record<
    RequirementType,
    () => FormDefaultDataLoaderQueryResult
  > = {
    [RequirementType.BackgroundCheck]: () => {
      return {
        loading: backgroundChecksSettingsQuery.isLoading,
        error: backgroundChecksSettingsQuery.error,
        data: backgroundChecksSettingsQuery.data
          ? getBackgroundChecksDefaultValues(backgroundChecksSettingsQuery.data)
          : undefined,
        editError: editBackgroundChecksRequirementDefinition.error,
        onSubmit: editBackgroundChecksRequirementDefinition.onSubmit
      };
    },
    [RequirementType.SharedFile]: () => {
      return {
        loading: sharedFileSettingsQuery.isLoading,
        error: sharedFileSettingsQuery.error,
        data: sharedFileSettingsQuery.data
          ? getSharedFileDefaultValues(sharedFileSettingsQuery.data)
          : undefined,
        editError: editSharedFileRequirementDefinition.error,
        onSubmit: editSharedFileRequirementDefinition.onSubmit
      };
    },
    [RequirementType.Signature]: () => {
      return {
        loading: false,
        error: null,
        data: undefined,
        editError: null,
        onSubmit: async () => {}
      };
    },
    [RequirementType.IdentityVerification]: () => {
      return {
        loading: false,
        error: null,
        data: undefined,
        editError: null,
        onSubmit: async () => {}
      };
    },
    [RequirementType.InsuranceCoverage]: () => {
      return {
        loading: false,
        error: null,
        data: undefined,
        editError: null,
        onSubmit: async () => {}
      };
    },
    [RequirementType.Registration]: () => {
      return {
        loading: false,
        error: null,
        data: undefined,
        editError: null,
        onSubmit: async () => {}
      };
    },
    [RequirementType.TaxVerification]: () => {
      return {
        loading: false,
        error: null,
        data: undefined,
        editError: null,
        onSubmit: async () => {}
      };
    },
    [RequirementType.PayoutMethod]: () => {
      return {
        loading: false,
        error: null,
        data: undefined,
        editError: null,
        onSubmit: async () => {}
      };
    }
  };

  const { loading, error, data, editError, onSubmit } =
    hookMap[requirementType]();

  return <>{children({ loading, error, data, editError, onSubmit })}</>;
};
